import { createApp, type App } from 'vue';
import { createPinia } from 'pinia';
import ComponentApp from './App.vue';
import { bindReportDirective } from '@common/logic';
import { useEventBus } from '@common/logic';
import { EVENTS } from '@/common/bus';

// 检查是否已经挂载
function isMounted(el: HTMLElement | string): boolean {
  const container = typeof el === 'string' ? document.querySelector(el) : el;
  return container?.querySelector('.index-container') !== null;
}

class NavLoginPanelSDK {
  private app: App | null = null;
  private pinia = createPinia();
  private targetElement: HTMLElement | null = null;
  private panelElement: HTMLElement | null = null;

  mount(el: HTMLElement | string = '#mod_head_user'): boolean {
    console.info('NavLoginPanelSDK mount called with:', el);

    if (isMounted(el)) {
      console.warn('NavLoginPanelSDK is already mounted');
      return false;
    }

    const container = typeof el === 'string' ? document.querySelector(el) : el;
    if (!container) {
      console.error(`Target element not found: ${el}`);
      return false;
    }

    this.targetElement = container as HTMLElement;

    // 创建面板容器
    this.createPanelContainer();

    // 创建应用实例
    this.app = createApp(ComponentApp);
    bindReportDirective(this.app);
    this.app.use(this.pinia);

    // 挂载到面板容器
    this.app.mount(this.panelElement!);

    // 设置 hover 事件
    this.setupHoverEvents();
    console.info('NavLoginPanelSDK mounted successfully');
    return true;
  }

  unmount(): void {
    if (this.app) {
      this.app.unmount();
      this.app = null;
    }

    if (this.panelElement && this.panelElement.parentNode) {
      this.panelElement.parentNode.removeChild(this.panelElement);
      this.panelElement = null;
    }

    this.targetElement = null;
  }

  // 重新挂载
  remount(el: HTMLElement | string = '#mod_head_user'): void {
    this.unmount();
    this.mount(el);
  }

  // 手动显示面板
  show(): void {
    this.showPanel();
  }

  // 手动隐藏面板
  hide(): void {
    this.hidePanel();
  }

  private createPanelContainer(): void {
    // 创建面板容器
    this.panelElement = document.createElement('div');
    this.panelElement.className = 'nav-login-panel-sdk';
    this.panelElement.style.cssText = `
      position: absolute;
      top: calc(100% + 6px);
      right: -10px;
      z-index: 9999;
      display: none;
      background: transparent;
      box-shadow: none;
      min-width: 320px;
    `;

    // 将面板添加到目标元素
    this.targetElement!.style.position = 'relative';
    this.targetElement!.appendChild(this.panelElement);
  }

  private setupHoverEvents(): void {
    if (!this.targetElement || !this.panelElement) return;

    let isHovering = false;

    // 鼠标进入挂载元素
    this.targetElement.addEventListener('mouseenter', () => {
      isHovering = true;
      this.showPanel();
    });

    // 鼠标离开挂载元素
    this.targetElement.addEventListener('mouseleave', (e) => {
      // 检查鼠标是否移动到面板上
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (relatedTarget && this.panelElement!.contains(relatedTarget)) {
        return;
      }
      isHovering = false;
      // 延迟隐藏，给鼠标移动到面板的时间
      setTimeout(() => {
        if (!isHovering) {
          this.hidePanel();
        }
      }, 200); // 200ms 延迟隐藏
    });

    // 鼠标进入面板
    this.panelElement.addEventListener('mouseenter', () => {
      isHovering = true;
    });

    // 鼠标离开面板
    this.panelElement.addEventListener('mouseleave', () => {
      isHovering = false;
      this.hidePanel();
    });
  }

  private showPanel(): void {
    if (this.panelElement && this.panelElement.style.display === 'none') {
      this.panelElement.style.display = 'block';
      useEventBus().$emit(EVENTS.SHOW_PANEL);
    }
  }

  private hidePanel(): void {
    if (this.panelElement) {
      this.panelElement.style.display = 'none';
    }
  }
}

export default new NavLoginPanelSDK();
